import React, { FC, useEffect } from "react";
import { Box, Link, Stack, SxProps, Typography } from "@mui/material";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";

interface Props {
  small?: boolean;
}

const timerTextStyle: SxProps = {
  fontFamily: "Martian Mono", // "Inter",
  fontWeight: {
    xs: 500,
    md: 700,
  },
  fontSize: {
    xs: "36px",
    sm: "48px",
    md: "48px",
  },
  lineHeight: {
    xs: "36px",
    sm: "48px",
    md: "48px",
  },
  background: `linear-gradient(63deg, #B4DAFF 7.18%, #E0BBFF 83.56%)`,
  backgroundClip: `text`,
  color: "transparent",
};

const timerSeparatorStyle: SxProps = {
  ...timerTextStyle,
  fontSize: {
    xs: "30px",
    sm: "50px",
    md: "50px",
  },
  fontWeight: 500,
};

const timerContainerStyle: SxProps = {
  position: "relative",
};

const timerLabelStyle: SxProps = {
  position: "absolute",
  fontWeight: 800,
  fontSize: {
    xs: "11px",
    md: "13px",
  },
  color: Colors.InfoLight,
  left: "50%",
  top: "calc(100% - 5px)",
  transform: `translate3d(-50%, 0px, 0px)`,
  textTransform: "uppercase",
};

function getPaddedString(time: number) {
  if (time < 10) {
    return `0${time}`;
  }

  return `${time}`;
}

const Countdown: FC<Props> = ({ small = false }) => {
  // March 3rd 12am ET
  const gameStartsMs = 1740978000000;

  const defaultWidth = {
    xs: "360px",
    sm: "450px",
  };
  const smallWidth = "120px";

  const [timerDays, setTimerDays] = React.useState(0);
  const [timerHours, setTimerHours] = React.useState(0);
  const [timerMinutes, setTimerMinutes] = React.useState(0);
  const [timerSeconds, setTimerSeconds] = React.useState(0);

  const [glowStyle, setGlowStyle] = React.useState<SxProps>({});

  const calculateGlowStyle = () => {
    setGlowStyle({
      position: "absolute",
      width: !small ? defaultWidth : smallWidth,
      height: !small ? "115px" : "30px",
      borderRadius: "30px",
    });
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = gameStartsMs - new Date().getTime();

      if (difference > 0) {
        setTimerDays(Math.floor(difference / (1000 * 60 * 60 * 24)));
        setTimerHours(Math.floor((difference / (1000 * 60 * 60)) % 24));
        setTimerMinutes(Math.floor((difference / 1000 / 60) % 60));
        setTimerMinutes(Math.floor((difference / 1000 / 60) % 60));
        setTimerSeconds(Math.floor((difference / 1000) % 60));
      } else {
        setTimerDays(0);
        setTimerHours(0);
        setTimerMinutes(0);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    calculateGlowStyle();

    return () => clearInterval(timer);
  }, [gameStartsMs]);

  useEffect(() => {
    calculateGlowStyle();
  }, [small]);

  return (
    <Link
      href="/landing/ai-escape-room/"
      sx={{
        display: "block",
        position: "relative",
        marginY: "80px",
        opacity: 1,
        textDecoration: "none",
        "&:hover": {
          opacity: 1, // need to override our default hover state,
          ".ai-challenge--countdown--highlight": {
            opacity: 1,
          },
          ".ai-challenge--counter": {
            borderColor: `rgba(211, 196, 235, 1) !important`,
          },
        },
      }}
    >
      {small ? (
        <Box
          sx={{
            ...glowStyle,
            boxShadow: `0px 0px 29px -4px rgba(151,99,232,1)`,
          }}
        />
      ) : (
        <>
          <Box
            sx={{
              ...glowStyle,
              boxShadow: `0px 0px 80px -15px rgba(255,255,255,0.2)`,
            }}
          />
          <Box
            sx={{
              ...glowStyle,
              boxShadow: `0px 0px 117px -20px rgba(255,255,255,0.7)`,
            }}
          />
          <Box
            className="ai-challenge--countdown--highlight"
            sx={{
              ...glowStyle,
              boxShadow: `0px 0px 117px -20px rgba(255,255,255,0.7)`,
              opacity: 0,
            }}
          />
          <Box
            sx={{
              ...glowStyle,
              boxShadow: `0px 13px 97px 17px #9763E8`,
            }}
          />
          <Box
            sx={{
              ...glowStyle,
              boxShadow: `0px 13px 37px 10pxrgb(138, 71, 247)`,
            }}
          />
          <Box
            className="ai-challenge--countdown--highlight"
            sx={{
              ...glowStyle,
              boxShadow: `0px 13px 37px 10pxrgb(138, 71, 247)`,
              opacity: 0,
            }}
          />
        </>
      )}
      <Box
        className="ai-challenge--counter"
        sx={{
          display: "flex",
          width: !small ? defaultWidth : smallWidth,
          height: !small ? "115px" : "30px",
          borderRadius: small ? "8px" : "30px",
          background: `linear-gradient(144deg, #23315A 9.13%, #111A34 62.89%)`,
          border: `solid ${small ? "1px" : "2px"} rgba(211, 196, 235, 0.6)`,
          borderStyle: "solid",
          boxShadow: `0px 0px 71px -1px rgba(255,255,255,0.3)`,
          justifyContent: "center",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            height: !small ? "115px" : "30px",
            transformOrigin: "50% 50%",
            transform: `${
              small
                ? "translate3d(0px, -3px, 0px) scale3d(0.25, 0.25, 1)"
                : "translate3d(0px, -12px, 0px)"
            }`,
          }}
          spacing={"0px"}
        >
          <Box
            sx={{
              ...timerContainerStyle,
            }}
          >
            <Typography
              sx={{
                ...timerTextStyle,
              }}
            >
              {getPaddedString(timerDays)}
            </Typography>
            <Typography
              sx={{
                ...timerLabelStyle,
              }}
            >
              days
            </Typography>
          </Box>

          <Typography
            sx={{
              ...timerSeparatorStyle,
            }}
          >
            :
          </Typography>

          <Box
            sx={{
              ...timerContainerStyle,
            }}
          >
            <Typography
              sx={{
                ...timerTextStyle,
              }}
            >
              {getPaddedString(timerHours)}
            </Typography>
            <Typography
              sx={{
                ...timerLabelStyle,
              }}
            >
              Hours
            </Typography>
          </Box>

          <Typography
            sx={{
              ...timerSeparatorStyle,
            }}
          >
            :
          </Typography>

          <Box
            sx={{
              ...timerContainerStyle,
            }}
          >
            <Typography
              sx={{
                ...timerTextStyle,
              }}
            >
              {getPaddedString(timerMinutes)}
            </Typography>
            <Typography
              sx={{
                ...timerLabelStyle,
              }}
            >
              Minutes
            </Typography>
          </Box>

          <Typography
            sx={{
              ...timerSeparatorStyle,
            }}
          >
            :
          </Typography>

          <Box
            sx={{
              ...timerContainerStyle,
            }}
          >
            <Typography
              sx={{
                ...timerTextStyle,
              }}
            >
              {getPaddedString(timerSeconds)}
            </Typography>
            <Typography
              sx={{
                ...timerLabelStyle,
              }}
            >
              Seconds
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Link>
  );
};

export default Countdown;
